import { loadWhatsAppSDK, parseWhatsAppSession, waitForWhatsAppSession } from '../utils/whatsappSignup';
import { useState, useEffect, useCallback, useRef, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faExclamationTriangle, faCircleInfo, faCircleCheck, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faWhatsapp, faTelegram } from "@fortawesome/free-brands-svg-icons";
import ManageConnectionsModal from "./ManageConnectionsModal";
import { ChannelModalContext } from '../contexts/ChannelModalContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import SelectBotModal from "./SelectBotModal";
import TelegramConnectModal from "./TelegramConnectModal";
import SlackConnectModal from "./SlackConnectModal";
import SlackLogo from "./SlackLogo";
import FacebookPermissionModal from "./FacebookPermissionModal";
import WhatsAppSetupModal from "./WhatsAppSetupModal";
import { guardWrite } from "../utils/permissions";
import { canSeeWhatsApp } from "../utils/featureFlags";
import "./socialMediaConnect.css";

function SocialMediaConnect({ variant = 'page', activeCategories = [], onlyPlatform = null }) {
  const modalFrame = useContext(ChannelModalContext);
  const { requestUnsavedAction } = useUnsavedChanges();
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activePlatform, setActivePlatform] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [selectBotModalOpen, setSelectBotModalOpen] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState(null);
  const [fbPermissionModalOpen, setFbPermissionModalOpen] = useState(false);
  const [telegramModalOpen, setTelegramModalOpen] = useState(false);
  const [slackModalOpen, setSlackModalOpen] = useState(false);
  // WhatsApp asks one extra question after the bot picker (which Embedded
  // Signup variant), so the chosen bot has to survive until that answer.
  const [waSetupModalOpen, setWaSetupModalOpen] = useState(false);
  const [waPendingBotId, setWaPendingBotId] = useState(null);
  const [waReplySettings, setWaReplySettings] = useState({ autoResponseMode: 'respond_all', pauseOnManualReply: true });

  // Use refs to store session data that can be accessed in callbacks
  const whatsappSessionDataRef = useRef(null);
  const oauthPopupRef = useRef(null);

  // Helper to check if we're inside an iframe
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true; // If we can't access window.top due to cross-origin, we're in an iframe
    }
  };

  // Listen for OAuth popup completion messages
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      // Accept messages from our own domain
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'oauth-success') {
        // OAuth completed successfully, refresh connections
        fetchConnections();

        // Close popup if still open
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
          oauthPopupRef.current.close();
        }
        oauthPopupRef.current = null;
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);

  // Check if we're in a popup and OAuth just completed - notify opener and close
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');

    if (connected && window.opener) {
      // We're in a popup after OAuth redirect - notify the parent window
      try {
        window.opener.postMessage(
          { type: 'oauth-success', platform: connected },
          window.location.origin
        );
      } catch {
        // If postMessage fails, parent will still refresh via polling
      }

      // Close the popup after a brief delay to ensure message is sent
      setTimeout(() => {
        window.close();
      }, 500);
    }
  }, []);

  // Listen for WhatsApp Embedded Signup session messages
  useEffect(() => {
    const handleMessage = (event) => {
      // Only accept messages from Facebook
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }

      try {
        const data = parseWhatsAppSession(event);
        if (!data) return;
        if (data.type === 'WA_EMBEDDED_SIGNUP') {

          if (['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(data.event)) {
            const { phone_number_id, waba_id } = data.data;
            // Store in ref so it's immediately available in callback
            whatsappSessionDataRef.current = { phone_number_id, waba_id };
          } else if (data.event === 'CANCEL') {
            const { current_step } = data.data;
            console.warn("WhatsApp signup cancelled at step:", current_step);
            setWhatsappLoading(false);
            whatsappSessionDataRef.current = null;
          } else if (data.event === 'ERROR') {
            const { error_message } = data.data;
            console.error("WhatsApp signup error:", error_message);
            setWhatsappLoading(false);
            whatsappSessionDataRef.current = null;
            alert(t('social_whatsapp_signup_error', { error: error_message }));
          }
        }
      } catch {
        // Non-JSON message, ignore
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Listen for reconnect-platform events from ManageConnectionsModal
  useEffect(() => {
    const handleReconnect = (event) => {
      const { platform, botId } = event.detail;
      if (platform === 'instagram') {
        handleInstagramConnect(botId);
      } else if (platform === 'facebook') {
        handleFacebookConnect(botId);
      } else if (platform === 'whatsapp') {
        if (!guardWrite()) return;
        if (botId) {
          setWaPendingBotId(botId);
          setWaSetupModalOpen(true);
        } else {
          setConnectPlatform('whatsapp');
          setSelectBotModalOpen(true);
        }
      } else if (platform === 'telegram') {
        setTelegramModalOpen(true);
      }
    };
    window.addEventListener('reconnect-platform', handleReconnect);
    return () => window.removeEventListener('reconnect-platform', handleReconnect);
  }, []);

  useEffect(() => {
    fetchConnections();
    
    // Check for Facebook permission errors in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const fbPermissionError = urlParams.get('fb_permission_error');
    
    if (fbPermissionError === 'no_pages' || fbPermissionError === 'subscription_failed') {
      setFbPermissionModalOpen(true);
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}messaging_connections?user_id=${userId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch connections");
      }

      const data = await response.json();
      setConnections(data);
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  };

  // Open the bot selection modal before connecting
  const handleConnectClick = (platform) => {
    // Read-only members get the unified permission-denied modal
    // immediately — don't even open the bot picker.
    if (!guardWrite()) return;
    // Telegram and Slack each have their own modal because they need
    // tokens/secrets the customer pastes (no OAuth). FB/IG/WhatsApp still
    // use the shared SelectBotModal because they're OAuth-driven.
    if (platform === 'telegram') {
      setTelegramModalOpen(true);
      return;
    }
    if (platform === 'slack') {
      setSlackModalOpen(true);
      return;
    }
    setConnectPlatform(platform);
    setSelectBotModalOpen(true);
  };

  const handleTelegramConnected = () => {
    setTelegramModalOpen(false);
    fetchConnections();
  };

  const handleSlackConnected = () => {
    setSlackModalOpen(false);
    fetchConnections();
  };

  // Handle bot selection confirmation
  const handleBotSelected = async (botId, autoResponseMode = 'respond_all', pauseOnManualReply = true) => {
    setSelectBotModalOpen(false);

    if (connectPlatform === 'facebook') {
      await handleFacebookConnect(botId, autoResponseMode, pauseOnManualReply);
    } else if (connectPlatform === 'instagram') {
      await handleInstagramConnect(botId, autoResponseMode, pauseOnManualReply);
    } else if (connectPlatform === 'whatsapp') {
      setWaReplySettings({ autoResponseMode, pauseOnManualReply });
      // Don't launch the wizard yet — ask which Embedded Signup variant first.
      setWaPendingBotId(botId);
      setWaSetupModalOpen(true);
      setConnectPlatform(null);
      return;
    }

    setConnectPlatform(null);
  };

  // The customer picked their WhatsApp situation; featureType decides which
  // Embedded Signup variant Meta opens (see WhatsAppSetupModal).
  const handleWhatsAppSetupSelected = async (featureType) => {
    setWaSetupModalOpen(false);
    const botId = waPendingBotId;
    setWaPendingBotId(null);
    if (!botId) return;
    await handleWhatsAppConnect(botId, featureType, waReplySettings);
  };

  // Third path: the customer already operates a Cloud API number and pastes
  // its phone_number_id + access token. Backend validates against Meta, runs
  // the same activation as the Embedded Signup callback, upserts the row.
  const handleWhatsAppManualConnect = async ({ phone_number_id, access_token, waba_id }) => {
    const botId = waPendingBotId;
    if (!botId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}messaging_connections/whatsapp/manual`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bot_id: botId, phone_number_id, access_token, waba_id, auto_response_mode: waReplySettings.autoResponseMode, pause_on_manual_reply: waReplySettings.pauseOnManualReply }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.detail || t('social_whatsapp_complete_failed'));
      }
      alert(t('social_whatsapp_connected', { phone: result.phone_number || 'N/A' }));
      const activation = result.activation;
      if (activation && (!activation.subscribed || !activation.registered)) {
        const detail = (activation.errors || []).join('; ') || `status=${activation.phone_status || 'unknown'}`;
        alert(t('social_whatsapp_complete_error', { error: detail }));
      }
      setWaSetupModalOpen(false);
      setWaPendingBotId(null);
      fetchConnections();
    } catch (error) {
      console.error('Error in manual WhatsApp connect:', error);
      alert(t('social_whatsapp_complete_error', { error: error.message }));
    }
  };

  const handleFacebookConnect = async (botId, autoResponseMode = 'respond_all', pauseOnManualReply = true) => {
    const userId = localStorage.getItem("user_id");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/facebook/connect?bot_id=${botId}&auto_response_mode=${encodeURIComponent(autoResponseMode)}&pause_on_manual_reply=${pauseOnManualReply}`
      );

      const data = await response.json();

      if (data.authorizeUrl) {
        if (isInIframe()) {
          // Open in popup window when inside iframe (e.g., Wix)
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          oauthPopupRef.current = window.open(
            data.authorizeUrl,
            'facebook-oauth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          // Poll to check if popup was closed manually
          const checkPopup = setInterval(() => {
            if (oauthPopupRef.current?.closed) {
              clearInterval(checkPopup);
              oauthPopupRef.current = null;
              // Refresh connections in case it succeeded
              fetchConnections();
            }
          }, 1000);
        } else {
          // Normal redirect for top-level window
          window.location.href = data.authorizeUrl;
        }
      }
    } catch (error) {
      console.error("Error connecting to Facebook:", error);
    }
  };

  const handleInstagramConnect = async (botId, autoResponseMode = 'respond_all', pauseOnManualReply = true) => {
    const userId = localStorage.getItem("user_id");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/instagram/connect?bot_id=${botId}&auto_response_mode=${encodeURIComponent(autoResponseMode)}&pause_on_manual_reply=${pauseOnManualReply}`
      );

      const data = await response.json();

      if (data.authorizeUrl) {
        if (isInIframe()) {
          // Open in popup window when inside iframe (e.g., Wix)
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          oauthPopupRef.current = window.open(
            data.authorizeUrl,
            'instagram-oauth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          // Poll to check if popup was closed manually
          const checkPopup = setInterval(() => {
            if (oauthPopupRef.current?.closed) {
              clearInterval(checkPopup);
              oauthPopupRef.current = null;
              // Refresh connections in case it succeeded
              fetchConnections();
            }
          }, 1000);
        } else {
          // Normal redirect for top-level window
          window.location.href = data.authorizeUrl;
        }
      }
    } catch (error) {
      console.error("Error connecting to Instagram:", error);
    }
  };

  const handleWhatsAppConnect = async (botId, featureType = '', replySettings = waReplySettings) => {
    const userId = localStorage.getItem("user_id");
    setWhatsappLoading(true);
    whatsappSessionDataRef.current = null;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/whatsapp/connect?bot_id=${encodeURIComponent(botId)}&feature_type=${encodeURIComponent(featureType)}&auto_response_mode=${encodeURIComponent(replySettings.autoResponseMode)}&pause_on_manual_reply=${replySettings.pauseOnManualReply}`
      );

      if (!response.ok) {
        throw new Error("Failed to get WhatsApp config");
      }

      const config = await response.json();

      // Load Facebook SDK
      await loadWhatsAppSDK(config.appId);

      // Launch WhatsApp Embedded Signup via FB.login
      window.FB.login(
        (fbResponse) => {
          if (fbResponse.authResponse) {
            const code = fbResponse.authResponse.code;

            const handleCallback = async () => {
              try {
                const sessionData = await waitForWhatsAppSession(whatsappSessionDataRef);
                const callbackResponse = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}auth/whatsapp/callback`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      code: code,
                      state: config.state,
                      waba_id: sessionData?.waba_id || null,
                      phone_number_id: sessionData?.phone_number_id || null,
                    }),
                  }
                );

                const result = await callbackResponse.json();

                if (callbackResponse.ok && result.success) {
                  alert(t('social_whatsapp_connected', { phone: result.phone_number || 'N/A' }));
                  // The backend also subscribes our app to the WABA and
                  // registers the number on the Cloud API. If either step
                  // failed the connection is saved but silent — say so now
                  // rather than letting the customer discover it by waiting.
                  const activation = result.activation;
                  if (activation && (!activation.subscribed || !activation.registered)) {
                    const detail = (activation.errors || []).join('; ') || `status=${activation.phone_status || 'unknown'}`;
                    alert(t('social_whatsapp_complete_error', { error: detail }));
                  }
                  // Meta-provided "display name only" numbers (+1 555 …) can
                  // receive immediately but refuse to SEND until Meta has
                  // approved the display name (Cloud API error 131037). Say
                  // so now, with the two steps that unblock it.
                  const phone = String(result.phone_number || '');
                  const isMetaProvided = /^\+?1[\s-]?555/.test(phone);
                  const nameApproved = activation && activation.name_status === 'APPROVED';
                  if (isMetaProvided && !nameApproved) {
                    alert(t('social_whatsapp_555_hint'));
                  }
                  fetchConnections();
                } else {
                  throw new Error(result.detail || t('social_whatsapp_complete_failed'));
                }
              } catch (callbackError) {
                console.error("Error in WhatsApp callback:", callbackError);
                alert(t('social_whatsapp_complete_error', { error: callbackError.message }));
              }

              setWhatsappLoading(false);
              whatsappSessionDataRef.current = null;
            };

            handleCallback();
          } else {
            setWhatsappLoading(false);
            whatsappSessionDataRef.current = null;
          }
        },
        {
          config_id: config.configId,
          response_type: 'code',
          override_default_response_type: true,
          // Mirrors what Meta's Embedded Signup Builder generates for our
          // login configuration: ES v4 (v2 is retired 2026-10-15) plus session
          // info v3. featureType is the ONLY difference between the two
          // onboarding variants and is omitted entirely for the classic flow,
          // exactly like the builder's "Feature Type: None".
          extras: {
            sessionInfoVersion: '3',
            version: 'v4',
            ...(featureType ? { featureType } : {})
          }
        }
      );
    } catch (error) {
      console.error("Error connecting to WhatsApp:", error);
      alert(t('social_generic_error', { error: error.message }));
      setWhatsappLoading(false);
      whatsappSessionDataRef.current = null;
    }
  };

  const handleManageConnections = (platform) => {
    // Managing a connection means delete / change / reconnect —
    // block read-only members up front.
    if (!guardWrite()) return;
    setActivePlatform(platform);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    fetchConnections();
  };

  const facebookConnections = connections.filter(c => c.platform === 'facebook');
  const instagramConnections = connections.filter(c => c.platform === 'instagram');
  const whatsappConnections = connections.filter(c => c.platform === 'whatsapp');
  const telegramConnections = connections.filter(c => c.platform === 'telegram');
  const slackConnections = connections.filter(c => c.platform === 'slack');

  const facebookCount = facebookConnections.length;
  const instagramCount = instagramConnections.length;
  const whatsappCount = whatsappConnections.length;
  const telegramCount = telegramConnections.length;
  const slackCount = slackConnections.length;

  const facebookUnassigned = facebookConnections.filter(c => !c.bot_id).length;
  const instagramUnassigned = instagramConnections.filter(c => !c.bot_id).length;
  const whatsappUnassigned = whatsappConnections.filter(c => !c.bot_id).length;
  const telegramUnassigned = telegramConnections.filter(c => !c.bot_id).length;
  const slackUnassigned = slackConnections.filter(c => !c.bot_id).length;

  const facebookExpired = facebookConnections.filter(c => c.status === 'token_expired').length;
  const instagramExpired = instagramConnections.filter(c => c.status === 'token_expired').length;

  const facebookHealthyAssigned = facebookConnections.filter(c => c.bot_id && c.status === 'active').length;
  const instagramHealthyAssigned = instagramConnections.filter(c => c.bot_id && c.status === 'active').length;
  const telegramHealthyAssigned = telegramConnections.filter(c => c.bot_id && c.status === 'active').length;
  const slackHealthyAssigned = slackConnections.filter(c => c.bot_id && c.status === 'active').length;

  // Every messaging platform card here belongs to the 'Messaging' category.
  const renderCards = () => {
    // In 'cards' mode, render nothing unless 'Messaging' is an active
    // category — unless a single platform is explicitly requested.
    if (variant === 'cards' && !onlyPlatform && !activeCategories.includes('Messaging')) {
      return null;
    }
    // When a single platform is requested, hide the others (keeps them
    // mounted so an in-flight connect flow is never torn down).
    const show = (p) => !onlyPlatform || onlyPlatform === p;

    return (
      <>
        <div className="social-platform-card" style={show('facebook') ? undefined : { display: 'none' }}>
          {facebookExpired > 0 && (
            <div className="expired-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#e53935', marginRight: '8px' }} />
              <span>{t('social_connections_expired', { count: facebookExpired })}</span>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleManageConnections('facebook')}
                sx={{
                  backgroundColor: '#e53935',
                  '&:hover': { backgroundColor: '#c62828' },
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: 'auto',
                  marginLeft: 'auto',
                  padding: '4px 12px',
                }}
              >
                {t('social_reconnect')}
              </Button>
            </div>
          )}
          {facebookHealthyAssigned > 0 && (
            <div className="assigned-success">
              <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5616ea', marginRight: '8px' }} />
              {t('social_connections_assigned', { count: facebookHealthyAssigned })}
            </div>
          )}
          {facebookUnassigned > 0 && (
            <div className="unassigned-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107', marginRight: '8px' }} />
              {t('social_connections_not_assigned', { count: facebookUnassigned })}
            </div>
          )}

          <div className="platform-card-content">
            <div className="platform-icon facebook">
              <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877f2' }} />
            </div>
            <h3>Facebook</h3>
            <p>{t('social_facebook_desc')}</p>

            {loading ? (
              <div style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#5616ea' }} />
              </div>
            ) : (
              <>
                <div className="button-group">
                  <Button
                    onClick={() => handleConnectClick('facebook')}
                    variant="contained"
                    sx={{
                      backgroundColor: '#5616ea',
                      '&:hover': { backgroundColor: '#4a14d6' },
                    }}
                    fullWidth
                  >
                    {t('social_connect_facebook')}
                  </Button>

                  {facebookCount === 0 ? (
                    <span className="disabled-button-wrapper">
                      <Button
                        onClick={() => handleManageConnections('facebook')}
                        variant="outlined"
                        disabled
                        sx={{
                          color: '#5616ea',
                          borderColor: '#5616ea',
                          '&.Mui-disabled': {
                            color: '#9e9e9e',
                            borderColor: '#e0e0e0',
                            backgroundColor: '#f8f8f8',
                          },
                        }}
                        fullWidth
                      >
                        {t('integrations_manage_connections_zero')}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      onClick={() => handleManageConnections('facebook')}
                      variant="outlined"
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                      }}
                      fullWidth
                    >
                      {t('integrations_manage_connections_count', { count: facebookCount })}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="social-platform-card" style={show('instagram') ? undefined : { display: 'none' }}>
          {instagramExpired > 0 && (
            <div className="expired-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#e53935', marginRight: '8px' }} />
              <span>{t('social_connections_expired', { count: instagramExpired })}</span>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleManageConnections('instagram')}
                sx={{
                  backgroundColor: '#e53935',
                  '&:hover': { backgroundColor: '#c62828' },
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: 'auto',
                  marginLeft: 'auto',
                  padding: '4px 12px',
                }}
              >
                {t('social_reconnect')}
              </Button>
            </div>
          )}
          {instagramHealthyAssigned > 0 && (
            <div className="assigned-success">
              <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5616ea', marginRight: '8px' }} />
              {t('social_connections_assigned', { count: instagramHealthyAssigned })}
            </div>
          )}
          {instagramUnassigned > 0 && (
            <div className="unassigned-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107', marginRight: '8px' }} />
              {t('social_connections_not_assigned', { count: instagramUnassigned })}
            </div>
          )}

          <div className="platform-card-content">
            <div className="platform-icon instagram">
              <FontAwesomeIcon icon={faInstagram} style={{ color: '#E4405F' }} />
            </div>
            <h3>Instagram</h3>
            <p>{t('social_instagram_desc')}</p>
            {loading ? (
              <div style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#5616ea' }} />
              </div>
            ) : (
              <>
                <div className="button-group">
                  <Button
                    onClick={() => handleConnectClick('instagram')}
                    variant="contained"
                    sx={{
                      backgroundColor: '#5616ea',
                      '&:hover': { backgroundColor: '#4a14d6' },
                    }}
                    fullWidth
                  >
                    {t('social_connect_instagram')}
                  </Button>

                  {instagramCount === 0 ? (
                    <span className="disabled-button-wrapper">
                      <Button
                        onClick={() => handleManageConnections('instagram')}
                        variant="outlined"
                        disabled
                        sx={{
                          color: '#5616ea',
                          borderColor: '#5616ea',
                          '&.Mui-disabled': {
                            color: '#9e9e9e',
                            borderColor: '#e0e0e0',
                            backgroundColor: '#f8f8f8',
                          },
                        }}
                        fullWidth
                      >
                        {t('integrations_manage_connections_zero')}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      onClick={() => handleManageConnections('instagram')}
                      variant="outlined"
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                      }}
                      fullWidth
                    >
                      {t('integrations_manage_connections_count', { count: instagramCount })}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="social-platform-card" style={show('telegram') ? undefined : { display: 'none' }}>
          {telegramHealthyAssigned > 0 && (
            <div className="assigned-success">
              <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5616ea', marginRight: '8px' }} />
              {t('social_connections_assigned', { count: telegramHealthyAssigned })}
            </div>
          )}
          {telegramUnassigned > 0 && (
            <div className="unassigned-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107', marginRight: '8px' }} />
              {t('social_connections_not_assigned', { count: telegramUnassigned })}
            </div>
          )}

          <div className="platform-card-content">
            <div className="platform-icon telegram">
              <FontAwesomeIcon icon={faTelegram} style={{ color: '#229ED9' }} />
            </div>
            <h3>Telegram</h3>
            <p>{t('social_telegram_desc')}</p>
            {loading ? (
              <div style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#5616ea' }} />
              </div>
            ) : (
              <div className="button-group">
                <Button
                  onClick={() => handleConnectClick('telegram')}
                  variant="contained"
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                  }}
                  fullWidth
                >
                  {t('connect_telegram')}
                </Button>

                {telegramCount === 0 ? (
                  <span className="disabled-button-wrapper">
                    <Button
                      onClick={() => handleManageConnections('telegram')}
                      variant="outlined"
                      disabled
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        '&.Mui-disabled': {
                          color: '#9e9e9e',
                          borderColor: '#e0e0e0',
                          backgroundColor: '#f8f8f8',
                        },
                      }}
                      fullWidth
                    >
                      {t('integrations_manage_connections_zero')}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handleManageConnections('telegram')}
                    variant="outlined"
                    sx={{
                      color: '#5616ea',
                      borderColor: '#5616ea',
                      '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                    }}
                    fullWidth
                  >
                    {t('integrations_manage_connections_count', { count: telegramCount })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="social-platform-card" style={show('slack') ? undefined : { display: 'none' }}>
          {slackHealthyAssigned > 0 && (
            <div className="assigned-success">
              <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5616ea', marginRight: '8px' }} />
              {t('social_connections_assigned', { count: slackHealthyAssigned })}
            </div>
          )}
          {slackUnassigned > 0 && (
            <div className="unassigned-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107', marginRight: '8px' }} />
              {t('social_connections_not_assigned', { count: slackUnassigned })}
            </div>
          )}

          <div className="platform-card-content">
            <div className="platform-icon slack">
              <SlackLogo size={64} />
            </div>
            <h3>Slack</h3>
            <p>{t('social_slack_desc')}</p>
            {loading ? (
              <div style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#5616ea' }} />
              </div>
            ) : (
              <div className="button-group">
                <Button
                  onClick={() => handleConnectClick('slack')}
                  variant="contained"
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                  }}
                  fullWidth
                >
                  {t('connect_slack')}
                </Button>

                {slackCount === 0 ? (
                  <span className="disabled-button-wrapper">
                    <Button
                      onClick={() => handleManageConnections('slack')}
                      variant="outlined"
                      disabled
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        '&.Mui-disabled': {
                          color: '#9e9e9e',
                          borderColor: '#e0e0e0',
                          backgroundColor: '#f8f8f8',
                        },
                      }}
                      fullWidth
                    >
                      {t('integrations_manage_connections_zero')}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handleManageConnections('slack')}
                    variant="outlined"
                    sx={{
                      color: '#5616ea',
                      borderColor: '#5616ea',
                      '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                    }}
                    fullWidth
                  >
                    {t('integrations_manage_connections_count', { count: slackCount })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* No expired-token banner here: the Embedded Signup login
            configuration issues a system user token that never expires, so
            WhatsApp has no reconnect state the way Facebook/Instagram do. */}
        <div className="social-platform-card" style={show('whatsapp') && canSeeWhatsApp() ? undefined : { display: 'none' }}>
          {whatsappUnassigned > 0 && (
            <div className="unassigned-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ffc107', marginRight: '8px' }} />
              {t('social_connections_not_assigned', { count: whatsappUnassigned })}
            </div>
          )}

          <div className="platform-card-content">
            <div className="platform-icon whatsapp">
              <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25D366' }} />
            </div>
            <h3>WhatsApp</h3>
            <p>{t('social_whatsapp_desc')}</p>

            {loading || whatsappLoading ? (
              <div style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#5616ea' }} />
              </div>
            ) : (
              <>
                <div className="button-group">
                  <Button
                    onClick={() => handleConnectClick('whatsapp')}
                    variant="contained"
                    sx={{
                      backgroundColor: '#5616ea',
                      '&:hover': { backgroundColor: '#4a14d6' },
                    }}
                    fullWidth
                  >
                    {t('social_connect_whatsapp')}
                  </Button>

                  {whatsappCount === 0 ? (
                    <span className="disabled-button-wrapper">
                      <Button
                        onClick={() => handleManageConnections('whatsapp')}
                        variant="outlined"
                        disabled
                        sx={{
                          color: '#5616ea',
                          borderColor: '#5616ea',
                          '&.Mui-disabled': {
                            color: '#9e9e9e',
                            borderColor: '#e0e0e0',
                            backgroundColor: '#f8f8f8',
                          },
                        }}
                        fullWidth
                      >
                        {t('integrations_manage_connections_zero')}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      onClick={() => handleManageConnections('whatsapp')}
                      variant="outlined"
                      sx={{
                        color: '#5616ea',
                        borderColor: '#5616ea',
                        '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                      }}
                      fullWidth
                    >
                      {t('integrations_manage_connections_count', { count: whatsappCount })}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  // Stable reference for the active platform's connections, so the inline
  // ManageConnectionsModal's `[open, connections]` effect doesn't refire (and
  // re-fetch bots) on every parent re-render — which otherwise spirals into a
  // "too many requests" loop with the modal's height animation.
  const activeConnections = useMemo(
    () => connections.filter((c) => c.platform === activePlatform),
    [connections, activePlatform]
  );

  const renderModals = () => (
    <>
      <ManageConnectionsModal
        open={isModalOpen}
        onClose={handleModalClose}
        connections={activeConnections}
        platform={activePlatform}
        onUpdate={fetchConnections}
        inline={!!onlyPlatform}
      />

      <SelectBotModal
        open={selectBotModalOpen}
        onClose={() => {
          setSelectBotModalOpen(false);
          setConnectPlatform(null);
        }}
        onConfirm={handleBotSelected}
        platform={connectPlatform}
      />

      <FacebookPermissionModal
        open={fbPermissionModalOpen}
        onClose={() => setFbPermissionModalOpen(false)}
      />

      <TelegramConnectModal
        open={telegramModalOpen}
        onClose={() => setTelegramModalOpen(false)}
        onConnected={handleTelegramConnected}
      />

      <SlackConnectModal
        open={slackModalOpen}
        onClose={() => setSlackModalOpen(false)}
        onConnected={handleSlackConnected}
      />

      <WhatsAppSetupModal
        open={waSetupModalOpen}
        onClose={() => {
          setWaSetupModalOpen(false);
          setWaPendingBotId(null);
        }}
        onSelect={handleWhatsAppSetupSelected}
        onManualConnect={handleWhatsAppManualConnect}
      />
    </>
  );

  // 'cards' mode: only the platform cards (a parent supplies the grid wrapper)
  // plus the modals, so an in-flight connect flow is never unmounted.
  if (variant === 'cards') {
    // In single-platform mode (Channels modal), "Manage connections" swaps the
    // card for the manage view inline (with a back button) instead of opening a
    // second modal behind the channel modal.
    const managing = !!onlyPlatform && isModalOpen && activePlatform === onlyPlatform;
    return (
      <>
        {managing && !modalFrame && (
          <button type="button" className="social-inline-back" onClick={() => requestUnsavedAction(handleModalClose)}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('back')}
          </button>
        )}
        {!managing && renderCards()}
        {renderModals()}
      </>
    );
  }

  // 'page' mode (default): render EXACTLY as before — zero visual change.
  return (
    <div className="social-media-connect-container">
      <h2>
        {t('social_page_title')}
        <span className="beta-badge">{t('beta_badge')}</span>
      </h2>

      <div className="social-info-box">
        <FontAwesomeIcon className="social-info-icon" icon={faCircleInfo} />
        <div className="social-info-content">
          <p>
            {t('social_info_box')}
          </p>
        </div>
      </div>

      <div className="social-platforms-grid">
        {renderCards()}
      </div>

      {renderModals()}
    </div>
  );
}

export default SocialMediaConnect;
